import { all, takeEvery, fork, put, select, call } from 'redux-saga/effects';
import MySubjectsService from '../../../services/mySubjects';
import StudentsService from '../../../services/students';
import { executeErrorCode } from '../../auth/actions';
import UrlBuilder, { getAttributes } from '../../../helpers/urlBuilder';
import { getTabData, getDynamicTabListFromStore } from '../selectors';
import { getColumnsConfig as getColumnsConfigAction } from '../../staticViews/students/actions';
import { getColumnsConfig as getColumnsConfigSaga } from '../../staticViews/students/saga';
import { getStaticViewState } from '../../staticViews/selectors';
import { stronaStudentaActionsInterfaces, stronaStudentaActionTypes, stronaStudentaActions } from '.';
import { dynamicTabsActions, dynamicTabsActionTypes } from '..';
import DataDestructuring from '../../../helpers/DataDestructuring';

const mandatoryFilters = ['semestrAkademicki', 'przedmiotId'];
const mandatoryColumns: string[] = [
  'przedmiotNazwa',
  'semestrAkademicki',
  'rokAkademicki',
  'wydzialZlecajacyKod',
  'przedmiotId',
  'przedmiotKod',
  'typPrzydzialu',
];

export function* initStronaStudentaWatcher(): Generator {
  yield takeEvery(stronaStudentaActionTypes.INIT, initStronaStudenta);
}
/**
 * inicjalizacja pobierania danych do zakładki strona-studenta
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* initStronaStudenta(action: stronaStudentaActionsInterfaces.Init): Generator {
  const { tabId } = action.payload;
  /*
    czekamy, aż pobiorą się informacje o studencie, którego dotyczy zakładka oraz konfiguracja kolumn dla tabeli "Przedmioty".
    - kolejne requesty wymagają pewnych informacji o studencie
    - zakładka ma swoją niezależną konfigurację kolumn.
    */
  yield all([
    call(getStudent, stronaStudentaActions.getLeftPanelData(tabId)),
    call(getColumnsInSingleStudent, stronaStudentaActions.getColumnsConfig(tabId)),
  ]);
  /*
  Lista przedmiotów jest pobierana dopiero, gdy dostępne są wszystkie dane:
  - informacje o studencie, dla którego pobierana jest lista,
  - konfiguracja kolumn dla tabeli "Przedmioty",
*/
  yield put(dynamicTabsActions.setLoading(tabId));
  yield call(getSubjectsInSingleStudent, stronaStudentaActions.getData(tabId));

  yield put(dynamicTabsActions.initTableFinish(tabId));
  yield put(dynamicTabsActions.initFinish(tabId));
}

export function* getColumnsInSingleStudentWatcher(): Generator {
  yield takeEvery(stronaStudentaActionTypes.COLUMNS_GET_CONFIG_START, getColumnsInSingleStudent);
}

/**
 * pobieranie konfiguracji kolumn - zakładka posiada oddzielną konfigurację od reszty aplikacji
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getColumnsInSingleStudent(action: stronaStudentaActionsInterfaces.GetColumnsConfig): Generator {
  const { tabId } = action.payload;
  /* najpierw sprawdzamy, czy w dowolnej zakładce o typie strona-studenta istnieje już config kolumn */
  const tabs = (yield select(getDynamicTabListFromStore)) as TabList;
  const tabWithConfig = tabs.find(
    tab => tab.tabType === 'strona-studenta' && tab.table.columns.length,
  ) as DynamicTabWithTable<StudentsData>;
  if (tabWithConfig) {
    yield put(dynamicTabsActions.getColumnsConfigSuccess(tabId, tabWithConfig.table.columns));
    yield put(dynamicTabsActions.saveColumnsUserSetupSuccess(tabId, tabWithConfig.table.columns));
  } else {
    try {
      const response: any = yield call(StudentsService.GetSubjectColumns);
      yield put(dynamicTabsActions.getColumnsConfigSuccess(tabId, response.przedmiotField));
      yield put(dynamicTabsActions.saveColumnsUserSetupSuccess(tabId, response.przedmiotField));
    } catch (error) {
      yield put(dynamicTabsActions.getColumnsConfigFail(tabId));
      if (error && error.errorCode) {
        yield put(executeErrorCode(error.errorCode, action, error));
      }
    }
  }
}

export function* getStudentWatcher(): Generator {
  yield takeEvery(stronaStudentaActionTypes.GET_LEFT_PANEL_DATA_START, getStudent);
}
/**
 * pobieranie danych o studencie, którego dotyczy zakładka
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudent(action: stronaStudentaActionsInterfaces.GetLeftPanelData): Generator {
  const { tabId } = action.payload;
  try {
    const { loading, columns, data } = (yield select(state => getStaticViewState(state, 'Students'))) as ViewDataSet;
    /*
      Pobieranie configu kolumn dla "Students".
      Konfiguracja kolumn jest niezbędna do wyświetlania danych o przedmiocie po lewej stronie zakładki pojedynczego studenta
      (StudentPage).
      - uruchamiany, jeśli nie jest on dostępny w redux staticViews -> Students
    */
    yield all([columns.length === 0 && !loading ? call(getColumnsConfigSaga, getColumnsConfigAction('Students')) : null]);
    const { itemId } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<StudentsData>;
    const [studentId, semestrAkademicki, przedmiotId] = itemId.toString().split('_');
    /*
      Jeśli dane o studencie są już dostępne (wcześniej została zainicjalizowana zakładka Studenci
      a strona została uruchomiona kliknięciem w rekord na liście) - informacje o studencie przepisywane są z listy "Studenci".
      W przypadku inicjalizacji zakładki z linku lub odświeżeniu strony wysyłany jest request o dane.
      W ten sposób zapobiegamy wysyłaniu niepotrzebnego requestu o dane, które są już dostępne w aplikacji i przyspieszamy inicjalizację.
    */
    if (data.length) {
      const recordFromList = data.find(record => record.studentId.toString() === studentId) as StudentsData;
      yield put(
        dynamicTabsActions.getLeftPanelDataSuccess(
          tabId,
          recordFromList,
          recordFromList.studentImie + ' ' + recordFromList.studentNazwisko,
        ),
      );
    } else {
      /*
        Pobieramy kolumny jeszcze raz ze stanu "Students", bo jeśli wcześniej tablica była pusta,
        to po wywołaniu sagi pobierającej config, powinny być już dostępne
      */
      const { columns } = (yield select(state => getStaticViewState(state, 'Students'))) as ViewDataSet;
      const requestParams = {
        id: studentId,
        columns: columns.map(column => column.dataIndex),
        // fixme: mockuje tyPrzydzialu, aby uzyskac dane!!! Powinno to zostac poprawione ze strony backendu.
        filter: UrlBuilder.generateFiltersArray({ semestrAkademicki, przedmiotId, typPrzydzialu: 'przydzial_podstawowy' }),
      };
      const response: any = yield call(StudentsService.getStudentDetails, requestParams);
      yield put(
        dynamicTabsActions.getLeftPanelDataSuccess(
          tabId,
          response.student,
          response.student.studentImie + ' ' + response.student.studentNazwisko,
        ),
      );
    }
  } catch (error) {
    yield put(dynamicTabsActions.getLeftPanelDataFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getSubjectsInSingleStudentWatcher(): Generator {
  yield takeEvery(stronaStudentaActionTypes.GET_DATA_START, getSubjectsInSingleStudent);
}
/**
 * pobieranie danych do tabeli "Przedmioty"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getSubjectsInSingleStudent(action: stronaStudentaActionsInterfaces.GetData): Generator {
  const { tabId } = action.payload;
  try {
    const { itemId, table } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<StudentsData>;

    const { filters, sorting } = table;
    const [studentId, semester] = itemId.toString().split('_');
    const columns = DataDestructuring.prepareColumnsAsParam(table.userColumnsSetup.main, mandatoryColumns);
    const requestAtributes: getAttributes = {
      filter: [
        ...filters.allFilters.map(filters => ({ name: filters.name, values: filters.selected })),
        { name: mandatoryFilters[0], values: [semester] },
      ],
      page: '1',
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      studentId,
      semester,
    };
    const response: any = yield call(StudentsService.getSubjectsForStudent, requestAtributes);
    /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
    const payload: GetDataSuccessPayload = {
      ...response.przedmiotPage,
      cacheDuration: response.cacheTime,
      controlSum: response.controlSum,
      data: response.przedmiotPage.data.map((obj: any) => ({
        ...obj,
        key: obj.przedmiotId,
      })),
    };
    yield put(dynamicTabsActions.getDataSuccess(tabId, payload));
  } catch (error) {
    yield put(dynamicTabsActions.getDataFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getSubjectsNextPageWatcher(): Generator {
  yield takeEvery(stronaStudentaActionTypes.GET_DATA_NEXT_PAGE_START, getSubjectsNextPage);
}
/**
 * dobieranie kolejnej partii danych do tabeli "Przedmioty"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getSubjectsNextPage(action: stronaStudentaActionsInterfaces.GetDataNextPage): Generator {
  const { tabId } = action.payload;
  try {
    const { itemId, table } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<StudentsData>;
    const { filters, sorting, page } = table;
    const [studentId, semestrAkademicki] = itemId.toString().split('_');
    const columns = DataDestructuring.prepareColumnsAsParam(table.userColumnsSetup.main, mandatoryColumns);
    const requestAtributes: getAttributes = {
      filter: [
        ...filters.allFilters.map(filters => ({ name: filters.name, values: filters.selected })),
        ...UrlBuilder.generateFiltersArray({ semestrAkademicki }),
      ],
      page: page + 1,
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      studentId,
      semester: semestrAkademicki,
    };
    const response: any = yield call(StudentsService.getSubjectsForStudent, requestAtributes);
    yield put(dynamicTabsActions.getDataNextPageSuccess(tabId, response.przedmiotPage));
    yield put(dynamicTabsActions.setPage(tabId, page + 1));
  } catch (error) {
    yield put(dynamicTabsActions.getDataNextPageFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getSubjectDetailsWatcher(): Generator {
  yield takeEvery([stronaStudentaActionTypes.RECORD_GET_DETAILS_START], getSubjectDetails);
}
export function* getSubjectDetails(action: stronaStudentaActionsInterfaces.GetRecordDetails): Generator {
  const { tabId, recordId, refresh } = action.payload;
  try {
    const { itemId, table } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<StudentsData>;
    const [studentId, semestrAkademicki] = itemId.toString().split('_');
    const columns = DataDestructuring.prepareColumnsAsParam(
      [...table.userColumnsSetup.details, 'wydzialZlecajacyNazwa', 'wydzialZlecajacyKod', 'studentZdjecie'],
      mandatoryColumns,
    );
    const selectedRow: any = table.data.find((row: any) => row.przedmiotId === recordId);
    if (refresh || !selectedRow.details || selectedRow.details.length === 0) {
      const response: any = yield MySubjectsService.getMySubjectDetailsInStudentPage({
        id: recordId,
        columns: columns,
        semestr: semestrAkademicki,
      });
      //pobieranie ocen studenta dla danego przedmiotu
      const marksColumns: string[] = [
        'oceny',
        'kartaTypKod',
        'ocenaKoncowa',
        'formyZajec',
        'ocenaWyliczona',
        'kartaTyp',
        'ocenaKoncowaData',
        'kartaOcenId',
      ];
      const marksResponse: any = yield call(StudentsService.getStudentDetails, {
        id: studentId,
        columns: marksColumns,
        filter: UrlBuilder.generateFiltersArray({ semestrAkademicki, przedmiotId: recordId }),
      });
      // verify - sprawdz poprawnosc payloadu - prawdopodobnie trzeba bedzie go zbudować zgodnie z tym czego oczekuje reducer dynamicTabsActionTypes.RECORD_GET_DETAILS_SUCCESS
      yield put(dynamicTabsActions.getRecordDetailsSuccess(tabId, response.przedmiot, marksResponse.student, recordId));
    } else {
      yield put(dynamicTabsActions.setDetailsLoading(tabId));
    }
    // }
  } catch (error) {
    yield put(dynamicTabsActions.getRecordDetailsFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* studentPageSetColumnsWatcher(): Generator {
  yield takeEvery(stronaStudentaActionTypes.COLUMNS_SAVE_USER_SETUP_START, studentPageSetColumns);
}
export function* studentPageSetColumns(action: stronaStudentaActionsInterfaces.SaveColumnsUserSetup): Generator {
  const { tabId, selectedColumns } = action.payload;

  try {
    yield call(StudentsService.setStudentPageSetup, { columnsSetup: selectedColumns });
    yield call(getColumnsInSingleStudent, stronaStudentaActions.getColumnsConfig(tabId));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* refreshTabsDataAfterChangeColumnsSetupWatcher(): Generator {
  yield takeEvery([dynamicTabsActionTypes.COLUMNS_SAVE_USER_SETUP_SUCCESS], refreshTabsDataAfterChangeColumnsSetup);
}
/**
 * inicjalizacja odświeżenia danych po zmianie konfiguracji kolumn.
 * Wykonywana dla każdej otwartej zakładki o typie 'strona-studenta'
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshTabsDataAfterChangeColumnsSetup(): Generator {
  const tabs = (yield select(getDynamicTabListFromStore)) as TabList;
  for (const iterator of tabs) {
    if (!iterator.initTable && iterator.tabType === 'strona-studenta' && iterator.wasInitialized) {
      yield put(dynamicTabsActions.setLoading(iterator.id));
      yield put(stronaStudentaActions.getData(iterator.id));
      if (iterator.table.expanded) {
        yield put(stronaStudentaActions.getRecordDetails(iterator.id, iterator.table.expanded, true));
      }
    }
  }
}

export function* refreshSubjectsListWatcher(): Generator {
  yield takeEvery([stronaStudentaActionTypes.REFRESH_DATA_START, dynamicTabsActionTypes.SET_ORDER], refreshSubjectsList);
}
export function* refreshSubjectsList(action: stronaStudentaActionsInterfaces.RefreshData): Generator {
  const { tabId } = action.payload;
  const { table, leftPanel, initTable } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<StudentsData>;
  if (!table.initTable && Object.keys(leftPanel.data).length && !initTable) {
    yield put(stronaStudentaActions.getData(tabId));
    if (table.expanded) {
      yield put(dynamicTabsActions.setDetailsLoading(tabId));
      yield put(stronaStudentaActions.getRecordDetails(tabId, table.expanded, true));
    }
  }
}

export default function* rootSaga(): Generator {
  yield all([
    fork(initStronaStudentaWatcher),
    fork(getStudentWatcher),
    fork(getSubjectsInSingleStudentWatcher),
    fork(refreshTabsDataAfterChangeColumnsSetupWatcher),
    fork(getSubjectsNextPageWatcher),
    fork(getSubjectDetailsWatcher),
    fork(getColumnsInSingleStudentWatcher),
    fork(studentPageSetColumnsWatcher),
    fork(refreshSubjectsListWatcher),
  ]);
}
